import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Control } from 'react-hook-form';
import i18next from 'i18next';
import { formatDate } from 'components/DateTime';
import { formatPrice } from 'components/Price';
import { formatPercentage } from 'components/Percentage';
import { FormInputCurrency } from 'components/form/FormInputCurrency';
import { NO_PRICE } from 'semshared/pricelist/quickprice';
import type { CancellationWaiveMode } from 'semshared/offer/cancellationWaiver';

/** Preview shape from `cancellationWaiverPreview` (typed loosely until codegen catches up). */
export interface PremiumInvoiceCancellationWaiverPreview {
  cancellationFees: number;
  waiverBasis: number;
  waiverBasisFirstConfirmedAt?: string | Date | null;
  cancellationWaiver: number;
  sum: number;
  toleranceRate: number;
  priceOnRequest: boolean;
  hasFeesSnapshot: boolean;
  phases: Array<{
    daysToEvent: number;
    rate: number;
    cancelledTotal: number;
    addedTotal: number;
    cancellationBasis: number;
    cancellationCosts: number;
    priceOnRequest: boolean;
  }>;
  freePeriodDetails: Array<{ serviceName: string }>;
}

const tooltipSx = {
  maxWidth: 420,
  bgcolor: 'white',
  color: 'text.primary',
  boxShadow: 3,
  border: '1px solid',
  borderColor: 'divider',
  p: 1.5,
  fontSize: '0.875rem',
};

function formatMoney(amount: number, currency: string): string {
  if (amount === NO_PRICE) {
    return i18next.t('invoice-cancellation-waiver-price-on-request');
  }
  return formatPrice(amount, i18next.language || 'de-AT', currency);
}

function FeesTooltipContent({
  preview,
  currency,
}: {
  preview: PremiumInvoiceCancellationWaiverPreview;
  currency: string;
}) {
  if (!preview.hasFeesSnapshot || preview.phases.length === 0) {
    return (
      <Typography variant="body2">
        {preview.hasFeesSnapshot
          ? formatMoney(preview.cancellationFees, currency)
          : i18next.t('invoice-cancellation-waiver-fees-empty')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260 }}>
      {preview.phases.map(phase => (
        <Box key={`${phase.daysToEvent}-${phase.rate}`}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {i18next.t('invoice-cancellation-waiver-phase', {
              days: phase.daysToEvent,
              rate: formatPercentage(phase.rate, 0),
            })}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 2, rowGap: 0.25 }}>
            <Typography variant="body2">{i18next.t('invoice-cancellation-waiver-cancelled')}</Typography>
            <Typography variant="body2" align="right">
              {formatMoney(phase.cancelledTotal, currency)}
            </Typography>
            <Typography variant="body2">{i18next.t('invoice-cancellation-waiver-added')}</Typography>
            <Typography variant="body2" align="right">
              {formatMoney(phase.addedTotal, currency)}
            </Typography>
            <Typography variant="body2">{i18next.t('invoice-cancellation-waiver-basis')}</Typography>
            <Typography variant="body2" align="right">
              {formatMoney(phase.cancellationBasis, currency)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {i18next.t('invoice-cancellation-waiver-phase-costs')}
            </Typography>
            <Typography variant="body2" align="right" fontWeight={600}>
              {formatMoney(phase.cancellationCosts, currency)}
            </Typography>
          </Box>
        </Box>
      ))}
      {preview.freePeriodDetails.length > 0 && (
        <Typography variant="body2">{i18next.t('invoice-cancellation-waiver-free-period')}</Typography>
      )}
      <Divider />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body2" fontWeight={700}>
          {i18next.t('invoice-cancellation-fees')}
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          {formatMoney(preview.cancellationFees, currency)}
        </Typography>
      </Box>
    </Box>
  );
}

function WaiverTooltipContent({
  preview,
  currency,
}: {
  preview: PremiumInvoiceCancellationWaiverPreview;
  currency: string;
}) {
  const confirmedAt = preview.waiverBasisFirstConfirmedAt
    ? formatDate(new Date(preview.waiverBasisFirstConfirmedAt))
    : '—';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 220 }}>
      <Typography variant="body2">
        {i18next.t('invoice-cancellation-waiver-tooltip-date', { date: confirmedAt })}
      </Typography>
      <Typography variant="body2">
        {i18next.t('invoice-cancellation-waiver-tooltip-basis', {
          amount: formatMoney(preview.waiverBasis, currency),
        })}
      </Typography>
      <Typography variant="body2">
        {i18next.t('invoice-cancellation-waiver-tooltip-rate', {
          rate: formatPercentage(preview.toleranceRate, 0),
          amount: formatMoney(preview.cancellationWaiver, currency),
        })}
      </Typography>
    </Box>
  );
}

function AmountRow({
  label,
  amount,
  currency,
  bold,
  infoTitle,
}: {
  label: string;
  amount: number;
  currency: string;
  bold?: boolean;
  infoTitle?: React.ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body1" fontWeight={bold ? 700 : 400}>
          {label}
        </Typography>
        {infoTitle && (
          <Tooltip
            title={infoTitle}
            arrow
            placement="right"
            componentsProps={{ tooltip: { sx: tooltipSx } }}
          >
            <IconButton size="small" aria-label={label} sx={{ p: 0.25 }}>
              <InfoIcon fontSize="small" color="action" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="body1" fontWeight={bold ? 700 : 400}>
        {formatMoney(amount, currency)}
      </Typography>
    </Box>
  );
}

/**
 * Premium invoice: fees + peak-booking waiver + Sum, then waive radios → Amount to invoice.
 * Shared by hotel approval and seminargo backoffice overwrite (SM-2745) on `invoice_premium`.
 */
export function PremiumInvoiceCancellationWaiverSection({
  preview,
  currency,
  control,
  disabled,
  waiveMode,
  amountToInvoice,
  onWaiveModeChange,
}: {
  preview: PremiumInvoiceCancellationWaiverPreview;
  currency: string;
  control: Control<any>;
  disabled?: boolean;
  waiveMode: CancellationWaiveMode;
  amountToInvoice: number;
  onWaiveModeChange: (mode: CancellationWaiveMode) => void;
}) {
  const sumCap = preview.sum === NO_PRICE ? 0 : Math.max(0, preview.sum);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
          {i18next.t('invoice-cancellation-fees-and-waiver')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8} md={6}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
          }}
        >
          <AmountRow
            label={i18next.t('invoice-cancellation-fees')}
            amount={preview.cancellationFees}
            currency={currency}
            infoTitle={<FeesTooltipContent preview={preview} currency={currency} />}
          />
          <AmountRow
            label={i18next.t('invoice-cancellation-waiver')}
            amount={preview.cancellationWaiver}
            currency={currency}
            infoTitle={<WaiverTooltipContent preview={preview} currency={currency} />}
          />
          <Divider sx={{ my: 0.5 }} />
          <AmountRow
            label={i18next.t('invoice-cancellation-waiver-sum')}
            amount={preview.sum}
            currency={currency}
            bold
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset" disabled={disabled || sumCap <= 0}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            {i18next.t('invoice-cancellation-waiver-ask')}
          </FormLabel>
          <RadioGroup
            value={waiveMode}
            onChange={e => onWaiveModeChange(e.target.value as CancellationWaiveMode)}
          >
            <FormControlLabel
              value="WAIVE"
              control={<Radio />}
              label={i18next.t('invoice-cancellation-waiver-yes-complete')}
            />
            <FormControlLabel
              value="PARTIAL"
              control={<Radio />}
              label={i18next.t('invoice-cancellation-waiver-partial')}
            />
            <FormControlLabel
              value="CHARGE"
              control={<Radio />}
              label={i18next.t('invoice-cancellation-waiver-no-full')}
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      {waiveMode === 'PARTIAL' && (
        <Grid item xs={12} sm={4} md={3}>
          <FormInputCurrency
            name="cancellationChargedAmount"
            label={i18next.t('invoice-cancellation-waiver-charged-amount')}
            currency={currency}
            control={control}
            disabled={disabled || sumCap <= 0}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={8} md={6}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Typography variant="body1" fontWeight={700}>
            {i18next.t('invoice-cancellation-waiver-amount-to-invoice')}
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {formatMoney(amountToInvoice, currency)}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
}
